import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { UAParser } from 'ua-parser-js';
import { FormattedMessage, useIntl } from 'react-intl';
import NotificationOverlay from '#/shared/components/NotificationOverlay';
import { formStackConfigType } from '#/user/formstackConfig';
import * as Styled from './styled';
import FieldWrapper from './FieldWrapper';
import FileInput from './FileInput';

function RiderFeedbackForm({
  formstackConfig,
  tripUrl,
  redirectAfterSubmit,
  redirectDelay,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [userAgentData, setUserAgentData] = useState('');
  const [formValid, setFormValid] = useState(false);

  useEffect(() => {
    const uaParser = new UAParser();

    setUserAgentData(
      Object.entries({
        browser: uaParser.getBrowser(),
        os: uaParser.getOS(),
        device: uaParser.getDevice(),
      })
        // filter out entries with no data and convert into human-readable string
        .filter(item => Object.values(item[1]).some(v => !!v))
        .map(
          ([k, v]) =>
            `${k}:\n${Object.entries(v)
              // remove deprecated attribute
              .filter(([nk]) => !['major'].includes(nk))
              .map(([nk, nv]) => `\t${nk}: ${nv}`)
              .join('\n')}`
        )
        .join('\n')
    );
  }, []);

  const redirectTimeoutIdRef = useRef(null);

  /* The timing and number of loads of the iframe is inconsistent across
  browsers (e.g. Firefox vs. Chrome) and on some loads calls to `history.back`
  do nothing in Firefox so this implementation ensures that the final load after
  the form submission will trigger the redirect and clears earlier redirects */
  const onIFrameLoad = () => {
    if (!isSubmitting && !submissionComplete) {
      return;
    }

    setIsSubmitting(false);
    setSubmissionComplete(true);

    if (redirectTimeoutIdRef.current) {
      clearTimeout(redirectTimeoutIdRef.current);
    }

    redirectTimeoutIdRef.current = setTimeout(
      redirectAfterSubmit,
      redirectDelay
    );
  };

  const { formConfig, targetFrame } = formstackConfig;
  const formRef = useRef();
  const validationTimeoutId = useRef(null);

  const validateForm = (delay = null) => {
    if (validationTimeoutId.current) {
      clearTimeout(validationTimeoutId.current);
    }

    const executeValidation = () => {
      const formFields = [
        ...formRef.current.querySelectorAll('[name]').values(),
      ];

      setFormValid(formFields.every(f => f.checkValidity()));
      setEmailError(
        !formFields.find(f => f.name === formConfig.email.name).checkValidity()
      );

      return null;
    };

    validationTimeoutId.current = delay
      ? setTimeout(executeValidation, delay)
      : executeValidation();
  };

  const intl = useIntl();

  /* This form is doing a lot of unconventional things to get around the fact
  that Formstack doesn't allow CORS requests. Native html form actions are used
  to make the request as they're not subject to the same origin policy. Since
  the form submit causes a redirect, an iframe is used to capture it so that the
  user doesn't leave the app, and the iframe's onLoad is used to detect when the
  request has completed and redirects to the previously visited route. The
  downside is that the response to the form submission can't be inspected so
  there are no assurances that the request was successful. Resources:

  https://stackoverflow.com/a/11425019/2167004
  https://stackoverflow.com/a/168488/2167004 */
  return (
    <>
      <Styled.FeedbackForm
        onSubmit={() => setIsSubmitting(true)}
        method="post"
        encType="multipart/form-data"
        action={formstackConfig.url}
        target={targetFrame}
        autoComplete="off"
        ref={formRef}
        onChange={() => validateForm(1000)}
        onBlur={() => validateForm()}
        onKeyPress={e => {
          // prevent form submissions when "Enter" key is pressed
          if (e.key === 'Enter') e.preventDefault();
        }}
      >
        <input type="hidden" name={formConfig.tripUrl.name} value={tripUrl} />
        <input
          type="hidden"
          name={formConfig.userAgentData.name}
          value={userAgentData}
          data-testid="userAgentInput"
        />
        <input
          type="hidden"
          name={formConfig.form.name}
          value={formConfig.form.value}
        />
        <input
          type="hidden"
          name={formConfig.viewKey.name}
          value={formConfig.viewKey.value}
        />
        <input
          type="hidden"
          name={formConfig.submit.name}
          value={formConfig.submit.value}
        />
        <FieldWrapper
          fieldConfig={formConfig.customerName}
          labelIntlId="name-label"
        >
          <Styled.FeedbackFormInput
            value={customerName}
            onChange={e => setCustomerName(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'feedback.customer-name-hint',
            })}
            data-testid="customerNameInput"
          />
        </FieldWrapper>
        <FieldWrapper
          fieldConfig={formConfig.description}
          labelIntlId="description-label"
        >
          <Styled.FeedbackFormTextArea
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'feedback.description-hint',
            })}
            data-testid="descriptionInput"
          />
        </FieldWrapper>
        <FieldWrapper fieldConfig={formConfig.email} labelIntlId="email-label">
          <Styled.FeedbackFormInput
            value={email}
            type="email"
            onChange={e => setEmail(e.target.value)}
            placeholder={intl.formatMessage({ id: 'feedback.email-hint' })}
            data-testid="emailInput"
          />
        </FieldWrapper>
        {emailError && (
          <Styled.FeedbackFormError data-testid="mailErrorMessage">
            <FormattedMessage id="alerts.invalid-email" />
          </Styled.FeedbackFormError>
        )}
        <FieldWrapper
          fieldConfig={formConfig.upload}
          labelIntlId="file-upload-label"
        >
          <FileInput />
        </FieldWrapper>
        <Styled.SubmitFeedbackButton
          active={formValid}
          onKeyDown={e => {
            if (e.key === 'Enter') e.target.click();
          }}
          data-testid="submitButton"
        >
          <FormattedMessage id="feedback.submit-button-text" />
        </Styled.SubmitFeedbackButton>
      </Styled.FeedbackForm>
      <NotificationOverlay
        visible={isSubmitting || submissionComplete}
        message={intl.formatMessage({
          id: `feedback.${isSubmitting ? 'submitting' : 'submitted'}`,
        })}
      />
      <iframe
        width="0"
        height="0"
        style={{ border: 'none' }}
        src="about:blank"
        name={targetFrame}
        id={targetFrame}
        title={targetFrame}
        onLoad={onIFrameLoad}
      />
    </>
  );
}

RiderFeedbackForm.propTypes = {
  /** URL of trip plan for which feedback is being submitted */
  tripUrl: PropTypes.string.isRequired,
  /** Configuration used to submit the values in this form to formstack */
  formstackConfig: formStackConfigType.isRequired,
  /** Function that redirects the user after alerts submittal */
  redirectAfterSubmit: PropTypes.func.isRequired,
  /**
   * Milliseconds that success message will be displayed after submission
   * completes before the redirect method is fired
   */
  redirectDelay: PropTypes.number,
};

RiderFeedbackForm.defaultProps = {
  redirectDelay: 2500,
};

export default RiderFeedbackForm;
